import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import {
  Youtube, Plus, Trash2, RefreshCw, Loader2, ExternalLink,
  Hash, Globe, Search, AlertCircle, CheckCircle2, Video
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminYouTubeChannels({ user }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");
  const [newPriority, setNewPriority] = useState(5);
  const [adding, setAdding] = useState(false);
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, sRes] = await Promise.all([
        authFetch(`${API}/video-repairs/channels`),
        authFetch(`${API}/video-repairs/settings`),
      ]);
      if (chRes.ok) setChannels(await chRes.json());
      if (sRes.ok) setSettings(await sRes.json());
    } catch (e) { toast.error("Errore caricamento dati"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!newId.trim() || !newName.trim()) { toast.error("Inserisci Channel ID e nome"); return; }
    setAdding(true);
    try {
      const res = await authFetch(`${API}/video-repairs/channels`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: newId.trim(), name: newName.trim(), priority: newPriority }),
      });
      if (res.ok) { toast.success("Canale aggiunto"); setNewId(""); setNewName(""); setNewPriority(5); fetchData(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Errore"); }
    } catch (e) { toast.error("Errore connessione"); }
    setAdding(false);
  };

  const handleToggle = async (channelId, enabled) => {
    try {
      await authFetch(`${API}/video-repairs/channels/${channelId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !enabled }),
      });
      setChannels(prev => prev.map(c => c.channel_id === channelId ? { ...c, enabled: !enabled } : c));
      toast.success(enabled ? "Canale disabilitato" : "Canale abilitato");
    } catch (e) { toast.error("Errore"); }
  };

  const handleDelete = async (channelId, name) => {
    if (!window.confirm(`Rimuovere il canale "${name}"?`)) return;
    try {
      await authFetch(`${API}/video-repairs/channels/${channelId}`, { method: "DELETE" });
      setChannels(prev => prev.filter(c => c.channel_id !== channelId));
      toast.success("Canale rimosso");
    } catch (e) { toast.error("Errore"); }
  };

  const handleRefreshFeeds = async () => {
    setRefreshing(true);
    try {
      const res = await authFetch(`${API}/video-repairs/refresh-feeds`, { method: "POST" });
      if (res.ok) { const d = await res.json(); toast.success(`${d.new_videos} nuovi video da ${d.channels_processed} canali`); fetchData(); }
    } catch (e) { toast.error("Errore aggiornamento"); }
    setRefreshing(false);
  };

  const handleSaveApiKey = async () => {
    setSavingKey(true);
    try {
      const res = await authFetch(`${API}/video-repairs/settings`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ youtube_api_key: apiKey }),
      });
      if (res.ok) { toast.success("API Key salvata"); setApiKey(""); fetchData(); }
    } catch (e) { toast.error("Errore"); }
    setSavingKey(false);
  };

  const handleSearchChannels = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) { toast.error("Inserisci almeno 2 caratteri"); return; }
    setSearching(true);
    try {
      const res = await authFetch(`${API}/video-repairs/channels/search-youtube?q=${encodeURIComponent(searchQuery.trim())}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
        if (data.length === 0) toast.info("Nessun canale trovato");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore ricerca");
      }
    } catch (e) { toast.error("Errore connessione"); }
    setSearching(false);
  };

  const handleAddFromSearch = async (ch) => {
    setAddingId(ch.channel_id);
    try {
      const res = await authFetch(`${API}/video-repairs/channels`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel_id: ch.channel_id, name: ch.name, priority: 5 }),
      });
      if (res.ok) {
        toast.success(`Canale "${ch.name}" aggiunto`);
        setSearchResults(prev => prev.map(r => r.channel_id === ch.channel_id ? { ...r, already_added: true } : r));
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore");
      }
    } catch (e) { toast.error("Errore connessione"); }
    setAddingId(null);
  };

  const totalVideos = channels.reduce((sum, c) => sum + (c.video_count || 0), 0);
  const activeChannels = channels.filter(c => c.enabled).length;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-youtube-channels">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestione Canali YouTube</h1>
          <p className="text-zinc-400 text-sm mt-1">Configura i canali tecnici per la libreria Video Riparazioni</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleRefreshFeeds} disabled={refreshing} data-testid="admin-refresh-feeds">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Aggiorna Feed RSS
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-500/15"><Youtube className="w-5 h-5 text-red-400" /></div>
            <div><p className="text-2xl font-bold text-white">{channels.length}</p><p className="text-xs text-zinc-500">Canali totali</p></div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-green-500/15"><CheckCircle2 className="w-5 h-5 text-green-400" /></div>
            <div><p className="text-2xl font-bold text-white">{activeChannels}</p><p className="text-xs text-zinc-500">Canali attivi</p></div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-500/15"><Video className="w-5 h-5 text-blue-400" /></div>
            <div><p className="text-2xl font-bold text-white">{totalVideos}</p><p className="text-xs text-zinc-500">Video in archivio</p></div>
          </CardContent>
        </Card>
      </div>

      {/* API Key Config */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Globe className="w-5 h-5 text-orange-400" /> YouTube API Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            {settings?.has_api_key ? (
              <span className="flex items-center gap-2 text-sm text-green-400"><CheckCircle2 className="w-4 h-4" /> API Key configurata {settings.api_key_source === "system" ? "(di sistema)" : "(personalizzata)"}</span>
            ) : (
              <span className="flex items-center gap-2 text-sm text-yellow-400"><AlertCircle className="w-4 h-4" /> Nessuna API Key - solo feed RSS attivi</span>
            )}
          </div>
          <div className="flex gap-3">
            <Input placeholder="AIzaSy..." value={apiKey} onChange={e => setApiKey(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" data-testid="admin-api-key-input" />
            <Button onClick={handleSaveApiKey} disabled={savingKey || !apiKey.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="admin-save-api-key">
              {savingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </div>
          <p className="text-xs text-zinc-600">Ottieni la chiave da <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">Google Cloud Console</a> → Abilita YouTube Data API v3</p>
        </CardContent>
      </Card>

      {/* Search & Add Channel */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Search className="w-5 h-5 text-orange-400" /> Cerca e Aggiungi Canali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* YouTube Search */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1.5">Cerca canali su YouTube</label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Es: riparazione auto, AUTODOC Italia, meccanica..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearchChannels()}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white text-sm"
                  data-testid="search-youtube-channels-input"
                />
              </div>
              <Button className="bg-red-600 hover:bg-red-700 text-white px-6" onClick={handleSearchChannels} disabled={searching || searchQuery.trim().length < 2} data-testid="search-youtube-channels-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Youtube className="w-4 h-4 mr-2" />}
                Cerca
              </Button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 border-t border-zinc-800 pt-3">
              <p className="text-xs text-zinc-500 font-medium">{searchResults.length} canali trovati</p>
              {searchResults.map(ch => (
                <div key={ch.channel_id} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600 transition-colors" data-testid={`search-result-${ch.channel_id}`}>
                  {ch.thumbnail ? (
                    <img src={ch.thumbnail} alt={ch.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0"><Youtube className="w-5 h-5 text-red-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{ch.name}</p>
                    {ch.description && <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{ch.description}</p>}
                    <code className="text-[10px] text-zinc-600 font-mono">{ch.channel_id}</code>
                  </div>
                  {ch.already_added ? (
                    <span className="flex items-center gap-1 text-xs text-green-400 flex-shrink-0 px-3 py-1.5 bg-green-500/10 rounded-lg border border-green-500/30">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Aggiunto
                    </span>
                  ) : (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0" onClick={() => handleAddFromSearch(ch)} disabled={addingId === ch.channel_id} data-testid={`add-search-result-${ch.channel_id}`}>
                      {addingId === ch.channel_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                      Aggiungi
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Manual Add (collapsed) */}
          <details className="border-t border-zinc-800 pt-3">
            <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors">Aggiungi manualmente con Channel ID</summary>
            <div className="grid grid-cols-12 gap-3 items-end mt-3">
              <div className="col-span-5">
                <label className="text-xs text-zinc-500 block mb-1">Channel ID</label>
                <Input placeholder="UC... (dall'URL del canale YouTube)" value={newId} onChange={e => setNewId(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm" data-testid="admin-channel-id-input" />
              </div>
              <div className="col-span-4">
                <label className="text-xs text-zinc-500 block mb-1">Nome Canale</label>
                <Input placeholder="Es: AUTODOC Italia" value={newName} onChange={e => setNewName(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="admin-channel-name-input" />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-zinc-500 block mb-1">Priorita</label>
                <Input type="number" min={1} max={10} value={newPriority} onChange={e => setNewPriority(parseInt(e.target.value) || 5)} className="bg-zinc-800 border-zinc-700 text-white text-sm text-center" data-testid="admin-channel-priority-input" />
              </div>
              <div className="col-span-2">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={handleAdd} disabled={adding} data-testid="admin-add-channel-btn">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Aggiungi
                </Button>
              </div>
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Channel List */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Youtube className="w-5 h-5 text-red-500" /> Canali Whitelistati ({channels.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs text-zinc-500 uppercase tracking-wider font-medium border-b border-zinc-800">
              <div className="col-span-4">Canale</div>
              <div className="col-span-3">Channel ID</div>
              <div className="col-span-1 text-center">Video</div>
              <div className="col-span-1 text-center">Priorita</div>
              <div className="col-span-1 text-center">Attivo</div>
              <div className="col-span-2 text-center">Azioni</div>
            </div>
            {channels.map(ch => (
              <div key={ch.channel_id} className={`grid grid-cols-12 gap-3 items-center px-4 py-3 rounded-lg border transition-colors ${ch.enabled ? "bg-zinc-800/30 border-zinc-800 hover:border-zinc-700" : "bg-zinc-900/50 border-zinc-800/50 opacity-60"}`} data-testid={`admin-channel-${ch.channel_id}`}>
                <div className="col-span-4 flex items-center gap-3">
                  <Youtube className={`w-5 h-5 flex-shrink-0 ${ch.enabled ? "text-red-500" : "text-zinc-600"}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium truncate">{ch.name}</p>
                    {ch.last_rss_update && <p className="text-[10px] text-zinc-600">Ultimo aggiornamento: {new Date(ch.last_rss_update).toLocaleDateString("it-IT")}</p>}
                  </div>
                </div>
                <div className="col-span-3">
                  <code className="text-[11px] text-zinc-500 font-mono truncate block">{ch.channel_id}</code>
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-sm text-white font-medium">{ch.video_count || 0}</span>
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-sm text-orange-400 font-medium">{ch.priority || 5}</span>
                </div>
                <div className="col-span-1 flex justify-center">
                  <Switch checked={ch.enabled} onCheckedChange={() => handleToggle(ch.channel_id, ch.enabled)} data-testid={`admin-toggle-${ch.channel_id}`} />
                </div>
                <div className="col-span-2 flex justify-center gap-2">
                  <a href={`https://www.youtube.com/channel/${ch.channel_id}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-zinc-700 text-zinc-500 hover:text-white transition-colors" title="Apri su YouTube">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => handleDelete(ch.channel_id, ch.name)} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`admin-delete-${ch.channel_id}`} title="Rimuovi canale">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
